import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import LoginModal from './components/modals/LoginModal';
import SignupModal from './components/modals/SignupModal';
import AdminLoginModal from './components/modals/AdminLoginModal';
import AdminSignupModal from './components/modals/AdminSignupModal';
import EmailVerificationModal from './components/modals/EmailVerificationModal';
import ForgotPasswordModal from './components/modals/ForgotPasswordModal';
import ResetPasswordModal from './components/modals/ResetPasswordModal';
import Home from './pages/Home';
import Industries from './pages/Industries';
import Platform from './pages/Platform';
import Pricing from './pages/Pricing';
import About from './pages/About';
import Contact from './pages/Contact';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import FeatureDetail from './pages/FeatureDetail';
import AuthCallback from './pages/AuthCallback';
import NotFound from './pages/NotFound';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppShell() {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isAdminLoginModalOpen, setIsAdminLoginModalOpen] = useState(false);
  const [isAdminSignupModalOpen, setIsAdminSignupModalOpen] = useState(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isEmailVerificationModalOpen, setIsEmailVerificationModalOpen] = useState(false);

  const [forgotPasswordUserType, setForgotPasswordUserType] = useState('user');
  const [userIdentifier, setUserIdentifier] = useState('');
  const [userIdentifierType, setUserIdentifierType] = useState('email');
  const [verificationContext, setVerificationContext] = useState(null);

  const closeModals = () => {
    setIsLoginModalOpen(false);
    setIsSignupModalOpen(false);
    setIsAdminLoginModalOpen(false);
    setIsAdminSignupModalOpen(false);
    setIsForgotPasswordModalOpen(false);
    setIsResetPasswordModalOpen(false);
    setIsEmailVerificationModalOpen(false);
  };

  const openLoginModal = (userType = 'user') => {
    closeModals();
    if (userType === 'admin') {
      setIsAdminLoginModalOpen(true);
    } else {
      setIsLoginModalOpen(true);
    }
  };

  const openSignupModal = (userType = 'user') => {
    closeModals();
    if (userType === 'admin') {
      setIsAdminSignupModalOpen(true);
    } else {
      setIsSignupModalOpen(true);
    }
  };

  const switchToLogin = () => {
    setIsSignupModalOpen(false);
    setIsLoginModalOpen(true);
  };

  const switchToSignup = () => {
    setIsLoginModalOpen(false);
    setIsSignupModalOpen(true);
  };

  const switchToAdminLogin = () => {
    setIsAdminSignupModalOpen(false);
    setIsAdminLoginModalOpen(true);
  };

  const switchToAdminSignup = () => {
    setIsAdminLoginModalOpen(false);
    setIsAdminSignupModalOpen(true);
  };

  const openForgotPassword = (userType) => {
    setForgotPasswordUserType(userType);
    closeModals();
    setIsForgotPasswordModalOpen(true);
  };

  return (
    <>
      <ScrollToTop />
      <Navbar openLoginModal={openLoginModal} openSignupModal={openSignupModal} />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/industries" element={<Industries />} />
          <Route path="/platform" element={<Platform />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/solutions/:slug" element={<FeatureDetail />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={closeModals}
        onSwitchToSignup={switchToSignup}
        onForgotPassword={() => openForgotPassword('user')}
      />
      <SignupModal
        isOpen={isSignupModalOpen}
        onClose={closeModals}
        onSwitchToLogin={switchToLogin}
        onEmailVerification={(context) => {
          setUserIdentifier(context.identifier);
          setUserIdentifierType(context.authType || 'email');
          setVerificationContext(context);
          closeModals();
          setIsEmailVerificationModalOpen(true);
        }}
      />
      <AdminLoginModal
        isOpen={isAdminLoginModalOpen}
        onClose={closeModals}
        onSwitchToSignup={switchToAdminSignup}
        onForgotPassword={() => openForgotPassword('admin')}
      />
      <AdminSignupModal
        isOpen={isAdminSignupModalOpen}
        onClose={closeModals}
        onSwitchToLogin={switchToAdminLogin}
        onAdminEmailVerification={(context) => {
          setUserIdentifier(context.identifier);
          setUserIdentifierType(context.authType || 'email');
          setVerificationContext(context);
          closeModals();
          setIsEmailVerificationModalOpen(true);
        }}
      />
      <EmailVerificationModal
        isOpen={isEmailVerificationModalOpen}
        onClose={closeModals}
        userEmail={userIdentifier}
        verificationContext={verificationContext}
        onVerifySuccess={() => {
          closeModals();
        }}
      />
      <ForgotPasswordModal
        isOpen={isForgotPasswordModalOpen}
        onClose={closeModals}
        userType={forgotPasswordUserType}
        onBackToLogin={() => openLoginModal(forgotPasswordUserType)}
        onResetPassword={(identifier, authType) => {
          setUserIdentifier(identifier);
          setUserIdentifierType(authType || 'email');
          closeModals();
          setIsResetPasswordModalOpen(true);
        }}
      />
      <ResetPasswordModal
        isOpen={isResetPasswordModalOpen}
        onClose={closeModals}
        userType={forgotPasswordUserType}
        userEmail={userIdentifierType === 'email' ? userIdentifier : undefined}
        userPhone={userIdentifierType === 'phone' ? userIdentifier : undefined}
        userIdentifierType={userIdentifierType}
        onBackToLogin={() => openLoginModal(forgotPasswordUserType)}
        onPasswordReset={() => openLoginModal(forgotPasswordUserType)}
      />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
